/**
 * Question helper utilities
 * Shared between web and mobile applications
 */

/**
 * Gets a question by ID from the questions list
 * @param {Object[]} questionsList - Full list of questions
 * @param {number} questionId - ID of the question to find
 * @returns {Object|null} Question object or null if not found
 */
export const getQuestionById = (questionsList, questionId) => {
    if (!questionsList || !Array.isArray(questionsList)) {
        return null;
    }
    return questionsList.find(question => question.id === questionId) || null;
};

/**
 * Gets the current question from a set based on index
 * @param {Object[]} questionSet - Array of questions in the current set
 * @param {Object[]} questionsList - Full list of all questions
 * @param {number} currentIndex - Current question index
 * @returns {Object|null} Current question with full data or null
 */
export const getCurrentQuestion = (questionSet, questionsList, currentIndex) => {
    if (!questionSet || !Array.isArray(questionSet) || currentIndex >= questionSet.length || currentIndex < 0) {
        return null;
    }

    const questionRef = questionSet[currentIndex];
    if (!questionRef || !questionRef.id) {
        return null;
    }

    const fullQuestion = getQuestionById(questionsList, questionRef.id);
    if (!fullQuestion) {
        return null;
    }

    return {
        question: fullQuestion,
        setIndex: currentIndex,
        setId: questionRef.set_id || null
    };
};

/**
 * Gets topic name from domain list by topic ID
 * @param {Object[]} appDomains - List of app domains/topics
 * @param {number} topicId - Topic ID to look up
 * @returns {string} Topic name or empty string if not found
 */
export const getTopicNameById = (appDomains, topicId) => {
    if (!appDomains || !Array.isArray(appDomains)) {
        return '';
    }

    const domain = appDomains.find(item => item.id === topicId);
    return domain?.domain_name || '';
};

/**
 * Gets topic ID from current question
 * @param {Object[]} questionSet - Array of questions in current set
 * @param {number} currentIndex - Current question index
 * @returns {number|null} Topic ID or null if not found
 */
export const getCurrentTopicId = (questionSet, currentIndex) => {
    if (!questionSet || !Array.isArray(questionSet) || currentIndex >= questionSet.length || currentIndex < 0) {
        return null;
    }

    const questionRef = questionSet[currentIndex];
    return questionRef?.topic_id || null;
};

/**
 * Calculates navigation boundaries for question set
 * @param {number} currentIndex - Current question index
 * @param {number} totalQuestions - Total number of questions in set
 * @returns {Object} Navigation state with canGoPrevious and canGoNext
 */
export const getNavigationState = (currentIndex, totalQuestions) => {
    return {
        canGoPrevious: currentIndex > 0,
        canGoNext: currentIndex < totalQuestions - 1,
        isFirst: currentIndex === 0,
        isLast: currentIndex === totalQuestions - 1,
        progress: totalQuestions > 0 ? ((currentIndex + 1) / totalQuestions) * 100 : 0
    };
};

/**
 * Gets the next question index based on current index and navigation mode
 * @param {number} currentIndex - Current question index
 * @param {number} totalQuestions - Total number of questions
 * @param {string} mode - Navigation mode: 'linear' or 'circular'
 * @returns {number} Next question index
 */
export const getNextQuestionIndex = (currentIndex, totalQuestions, mode = 'circular') => {
    if (mode === 'linear') {
        return currentIndex === totalQuestions - 1 ? currentIndex : currentIndex + 1;
    } else {
        return currentIndex === totalQuestions - 1 ? 0 : currentIndex + 1;
    }
};

/**
 * Gets the previous question index based on current index and navigation mode
 * @param {number} currentIndex - Current question index
 * @param {number} totalQuestions - Total number of questions
 * @param {string} mode - Navigation mode: 'linear' or 'circular'
 * @returns {number} Previous question index
 */
export const getPreviousQuestionIndex = (currentIndex, totalQuestions, mode = 'linear') => {
    if (mode === 'linear') {
        return currentIndex === 0 ? 0 : currentIndex - 1;
    } else {
        return currentIndex === 0 ? totalQuestions - 1 : currentIndex - 1;
    }
};

/**
 * Validates if a question set is valid
 * @param {Object[]} questionSet - Array of questions to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateQuestionSet = (questionSet) => {
    const errors = [];

    if (!questionSet) {
        errors.push('Question set is null or undefined');
    } else if (!Array.isArray(questionSet)) {
        errors.push('Question set must be an array');
    } else if (questionSet.length === 0) {
        errors.push('Question set is empty');
    } else {
        // Check each question has required properties
        questionSet.forEach((question, index) => {
            if (!question.id) {
                errors.push(`Question at index ${index} missing id`);
            }
        });
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Extracts the non-empty answer choices (choice_a .. choice_o) from a question.
 * The previously-duplicated 15-slot shaping shared by web (api/test) and mobile
 * (auth context). Returns [] for a falsy question.
 * @param {Object} question
 * @returns {string[]}
 */
export const extractChoices = (question) => {
    if (!question) return [];
    return [
        question?.choice_a ?? '',
        question?.choice_b ?? '',
        question?.choice_c ?? '',
        question?.choice_d ?? '',
        question?.choice_e ?? '',
        question?.choice_f ?? '',
        question?.choice_g ?? '',
        question?.choice_h ?? '',
        question?.choice_i ?? '',
        question?.choice_j ?? '',
        question?.choice_k ?? '',
        question?.choice_l ?? '',
        question?.choice_m ?? '',
        question?.choice_n ?? '',
        question?.choice_o ?? '',
    ].filter(choice => choice !== null && choice !== '');
};

/**
 * Canonical wrapped question lookup: find a question by id (loose `==` to match
 * legacy string/number ids) and shape it with its extracted choices. This is the
 * `{ question, choices }` form both apps already use (resolves the questionHelpers
 * name drift — web's api/test GET_QUESTION_FROM_ID re-exports this). Mobile keeps
 * its own Map-based lookup for performance and reuses `extractChoices`.
 * @param {Object[]} questions - Full list of questions
 * @param {number|string} id - Question id
 * @returns {{ question: (Object|undefined), choices: string[] }}
 */
export const getQuestionFromId = (questions, id) => {
    const question = Array.isArray(questions)
        ? questions.find(o => o.id == id)
        : undefined;
    return { question, choices: extractChoices(question) };
};